import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ForgotPasswordFormSchema } from '../../schema';

import { MessageEmail } from './components/MessageEmail';

import { Colors, Key } from '@/assets';
import { Alert, InputText, Loading, PrimaryButton, showToatify } from '@/components';
import { APIsendPasswordResetEmail, ForgotPasswordForm } from '@/features/auth';
import { classValidatorFormResolverFactory } from '@/helper';
import { useMessageData } from '@/hooks';
import { Screens } from '@/routes';

const ForgotPasswordFormSchemaValidatorResolver = classValidatorFormResolverFactory<ForgotPasswordFormSchema>(ForgotPasswordFormSchema);

export const ForgotPassword = () => {
    const navigate = useNavigate();
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();

    const [sentSuccessToEmail, setSendSuccessToEmail] = useState<string | undefined>();
    const [loading, setLoading] = useState<boolean>(false);

    const form = useForm<ForgotPasswordFormSchema>({
        resolver: ForgotPasswordFormSchemaValidatorResolver
    });

    const onSubmit = async (data: ForgotPasswordForm) => {
        try {
            setLoading(true);
            await APIsendPasswordResetEmail(data);
            localStorage.setItem('FORGOT_PASSWORD_EMAIL', data.email);
            showToatify('Email sent successfully');
            setSendSuccessToEmail(data.email);
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            setLoading(false);
        }
    };

    if (sentSuccessToEmail) {
        return <MessageEmail email={sentSuccessToEmail} />;
    }

    return (
        <FormProvider {...form}>
            <Loading loading={loading} />
            <Key />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue }}>
                Forgot password?
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 'normal', color: Colors.lynchBlue }}>
                No worries, we’ll send you reset instructions.
            </Typography>
            <form style={{ width: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ mt: 3 }}>
                    <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <InputText name="email" label="Email" placeholder="Enter your email" />
                        <PrimaryButton title="Reset password" submit />
                        <Button onClick={() => navigate(Screens.Login)} sx={{ color: Colors.lynchBlue, py: 1.5, boxShadow: 'none' }}>
                            <i className="far fa-arrow-left"></i>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: Colors.lynchBlue, ml: 0.5 }}>
                                Back to log in
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
